import { AuthApiService } from './../../../services/auth-api.service';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  signInWithCustomToken,
  signInWithPopup,
  UserCredential,
} from '@angular/fire/auth';
import { LoadingController, Platform } from '@ionic/angular';
import { ToastService } from '../../../services/toast.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from '../../../../../shared/services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '../../../../../shared/services/logger.service';
import { AuthService } from '../../../services/auth/auth.service';
import { switchMap } from 'rxjs';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { BrowserService } from '../../../services/in-app-services/browser.service';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  standalone: false,
})
export class SignInComponent implements OnInit {
  auth = inject(Auth);
  authService = inject(AuthService);
  platform = inject(Platform);
  toastService = inject(ToastService);
  router = inject(Router);
  authApiService = inject(AuthApiService);
  errorService = inject(ErrorService);
  loadingCtrl = inject(LoadingController);
  translate = inject(TranslateService);
  loggerService = inject(LoggerService);
  browserService = inject(BrowserService);

  showPassword = signal<boolean>(false);
  selectedLanguage = signal<string>(this.translate.currentLang);

  email = signal<string>('');
  password = signal<string>('');

  isIOS = Capacitor.getPlatform() === 'ios';

  private readonly languages = ['en', 'ge'];

  constructor() {}

  ngOnInit() {}

  toggleLanguage() {
    const currentIndex = this.languages.indexOf(this.selectedLanguage());
    const nextIndex = (currentIndex + 1) % this.languages.length;
    const nextLang = this.languages[nextIndex];

    this.translate.use(nextLang);
    this.selectedLanguage.set(nextLang);
  }

  togglePassword() {
    this.showPassword.set(!this.showPassword());
  }

  async signInWithGoogle() {
    this.showLoading();

    this.authService.shouldLoadStartPrintUser = false;

    let userCredentials: UserCredential | null;
    if (this.platform.is('ios') || this.platform.is('android')) {
      userCredentials = await this.signInWithGoogleIosOrAndroid();
    } else {
      userCredentials = await this.signInWithGoogleWeb();
    }

    if (!userCredentials) {
      this.stopLoading();
      return;
    }

    this.loggerService.logEvent('signInWithGoogle');

    this.handleMobileSignIn();
  }

  private async signInWithGoogleWeb() {
    try {
      const provider = new GoogleAuthProvider();
      const userCredentials = await signInWithPopup(this.auth, provider);

      return userCredentials;
    } catch (error) {
      this.loggerService.logException(error, 'Google Sign In Web Error');
      this.toastService.error(this.translate.instant('AUTH.SIGN_UP.GOOGLE_SIGN_IN_ERROR'));

      return null;
    }
  }

  private async signInWithGoogleIosOrAndroid() {
    try {
      const result = await FirebaseAuthentication.signInWithGoogle();

      const credential = GoogleAuthProvider.credential(result.credential?.idToken);
      const userCredentials = await signInWithCredential(this.auth, credential);

      return userCredentials;
    } catch (error) {
      this.loggerService.logException(error, 'Google Sign In Ios Or Android Error');
      this.toastService.error(this.translate.instant('AUTH.SIGN_UP.GOOGLE_SIGN_IN_ERROR'));

      return null;
    }
  }

  async signInWithApple() {
    try {
      this.authService.shouldLoadStartPrintUser = false;

      this.showLoading();
      const result = await FirebaseAuthentication.signInWithApple({
        skipNativeAuth: true,
      });
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: result.credential?.idToken,
        rawNonce: result.credential?.nonce,
      });
      const userCredentials = await signInWithCredential(this.auth, credential);

      if (!userCredentials) {
        return;
      }

      this.loggerService.logEvent('signInWithApple');

      this.handleMobileSignIn();
    } catch (error) {
      this.loggerService.logException(error, 'Apple Sign In Error');
      this.stopLoading();
    }
  }

  async signInWithCredentials() {
    if (!this.email()) {
      this.toastService.error(this.translate.instant('AUTH.SIGN_IN.EMAIL_REQUIRED'));
      return;
    }

    if (!this.password()) {
      this.toastService.error(this.translate.instant('AUTH.SIGN_IN.PASSWORD_REQUIRED'));
      return;
    }

    this.authService.shouldLoadStartPrintUser = true;
    this.showLoading();

    this.loggerService.logEvent('signInWithCredentials', {
      email: this.email(),
    });

    this.authApiService
      .loginClient({
        email: this.email(),
        password: this.password(),
      })
      .subscribe({
        next: async (idToken) => {
          await signInWithCustomToken(this.auth, idToken);

          this.email.set('');
          this.password.set('');

          this.router.navigate(['/home']);

          this.stopLoading();
        },
        error: (error: HttpErrorResponse) => {
          this.errorService.handleError(error);

          this.stopLoading();
        },
      });
  }

  async handleMobileSignIn() {
    const idToken = await this.auth.currentUser?.getIdToken();

    this.authApiService
      .handleSocialLogin(idToken!)
      .pipe(switchMap(() => this.authApiService.getStartPrintUser()))
      .subscribe({
        next: (user) => {
          this.authService.startPrintUser.set(user);
          this.authService.shouldLoadStartPrintUser = true;
          this.stopLoading();
          this.router.navigate(['/']);
        },
        error: (error: HttpErrorResponse) => {
          this.errorService.handleError(error);
          this.stopLoading();
        },
      });
  }

  private async showLoading() {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('COMMON.PLEASE_WAIT'),
      animated: true,
    });

    await loading.present();
  }

  private stopLoading() {
    this.loadingCtrl.dismiss();
  }

  openTerms(event: Event) {
    event.preventDefault();

    this.browserService.openTerms();
  }

  openPrivacy(event: Event) {
    event.preventDefault();

    this.browserService.openPrivacy();
  }
}
